import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeycloakProfile } from 'keycloak-js';
import config from 'config/config';
import {
    generatePKCEParams,
    getStoredCodeVerifier,
    clearStoredCodeVerifier,
    storeReturnPath,
    getStoredReturnPath,
    clearStoredReturnPath
} from 'utils/pkce';
import { registerLogoutHandler, unregisterLogoutHandler, configureAxios } from 'utils/axiosConfig';

interface TokenSet {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
}

interface StoredSession {
    tokens: TokenSet;
    user: KeycloakProfile;
}

interface AuthState {
    tokens: TokenSet | null;
    user: KeycloakProfile | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

export interface AuthContextValue {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: KeycloakProfile | null;
    error: string | null;
    accessToken: string | null;
    hasConfig: boolean;
    loginWithPassword: (username: string, password: string) => Promise<void>;
    loginWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'tammilanit.keycloak.session';

const hasRequiredConfig = () => Boolean(config.KEYCLOAK_URL && config.KEYCLOAK_REALM && config.KEYCLOAK_CLIENT_ID);

const realmBase =
    config.KEYCLOAK_URL && config.KEYCLOAK_REALM
        ? `${config.KEYCLOAK_URL.replace(/\/$/, '')}/realms/${config.KEYCLOAK_REALM}`
        : '';

interface TokenResponse {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    refresh_expires_in?: number;
    token_type: string;
    scope?: string;
}

const ensureConfig = () => {
    if (!hasRequiredConfig() || !realmBase) {
        throw new Error('Keycloak connection details are missing. Please configure the environment variables.');
    }
};

const DEFAULT_SCOPE = config.KEYCLOAK_SCOPE || 'openid profile email';

const buildBody = (params: Record<string, string>, includeScope = false) => {
    const body = new URLSearchParams(params);
    if (includeScope) {
        body.append('scope', DEFAULT_SCOPE);
        if (config.KEYCLOAK_AUDIENCE) {
            body.append('audience', config.KEYCLOAK_AUDIENCE);
        }
    }
    if (config.KEYCLOAK_CLIENT_SECRET) {
        body.append('client_secret', config.KEYCLOAK_CLIENT_SECRET);
    }
    return body;
};

const requestToken = async (body: URLSearchParams): Promise<TokenResponse> => {
    ensureConfig();
    const response = await fetch(`${realmBase}/protocol/openid-connect/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Keycloak token endpoint returned an error.');
    }
    return response.json();
};

const fetchUserProfile = async (accessToken: string): Promise<KeycloakProfile> => {
    ensureConfig();
    const response = await fetch(`${realmBase}/protocol/openid-connect/userinfo`, {
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
    if (!response.ok) {
        throw new Error('Unable to fetch user profile from Keycloak.');
    }
    return response.json();
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const [state, setState] = useState<AuthState>({
        tokens: null,
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null
    });
    const sessionRef = useRef<StoredSession | null>(null);
    const refreshTimerRef = useRef<number | null>(null);
    const refreshFnRef = useRef<((tokenOverride?: string) => Promise<void>) | null>(null);
    const handledOAuthCodeRef = useRef<string | null>(null);

    const hasConfig = hasRequiredConfig();

    const persistSession = useCallback((session: StoredSession | null) => {
        sessionRef.current = session;
        if (session) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const applySession = useCallback(
        (session: StoredSession | null) => {
            persistSession(session);
            setState(prev => ({
                ...prev,
                tokens: session?.tokens ?? null,
                user: session?.user ?? null,
                isAuthenticated: Boolean(session),
                isLoading: false,
                error: null
            }));
        },
        [persistSession]
    );

    const clearRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    }, []);

    const scheduleRefresh = useCallback(
        (tokens: TokenSet | null) => {
            clearRefreshTimer();
            if (!tokens) {
                return;
            }
            const refreshIn = Math.max(tokens.expiresAt - Date.now() - 30_000, 5_000);
            refreshTimerRef.current = window.setTimeout(() => {
                const refresher = refreshFnRef.current;
                if (refresher) {
                    refresher(tokens.refreshToken).catch(err => {
                        console.error('Keycloak refresh failed', err);
                        applySession(null);
                    });
                }
            }, refreshIn);
        },
        [applySession, clearRefreshTimer]
    );

    const refreshSession = useCallback(
        async (tokenOverride?: string) => {
            if (!hasConfig) {
                applySession(null);
                return;
            }
            const refreshToken = tokenOverride ?? sessionRef.current?.tokens.refreshToken;
            if (!refreshToken) {
                applySession(null);
                return;
            }
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            try {
                const tokenResponse = await requestToken(
                    buildBody(
                        {
                            grant_type: 'refresh_token',
                            refresh_token: refreshToken,
                            client_id: config.KEYCLOAK_CLIENT_ID
                        },
                        false
                    )
                );
                const nextRefresh = tokenResponse.refresh_token ?? refreshToken;
                const tokens: TokenSet = {
                    accessToken: tokenResponse.access_token,
                    refreshToken: nextRefresh,
                    expiresAt: Date.now() + tokenResponse.expires_in * 1000
                };
                const user = sessionRef.current?.user ?? (await fetchUserProfile(tokens.accessToken));
                applySession({ tokens, user });
                scheduleRefresh(tokens);
            } catch (error) {
                console.error('Failed to refresh Keycloak session', error);
                applySession(null);
                setState(prev => ({
                    ...prev,
                    error: error instanceof Error ? error.message : 'Failed to refresh the session.',
                    isLoading: false
                }));
            }
        },
        [applySession, hasConfig, scheduleRefresh]
    );

    useEffect(() => {
        refreshFnRef.current = refreshSession;
    }, [refreshSession]);

    const loginWithPassword = useCallback(
        async (username: string, password: string) => {
            if (!hasConfig) {
                setState(prev => ({
                    ...prev,
                    error: 'Keycloak connection details are missing. Configure the environment variables to enable login.'
                }));
                return;
            }
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            try {
                const tokenResponse = await requestToken(
                    buildBody(
                        {
                            grant_type: 'password',
                            username,
                            password,
                            client_id: config.KEYCLOAK_CLIENT_ID
                        },
                        true
                    )
                );
                const refreshToken = tokenResponse.refresh_token;
                if (!refreshToken) {
                    throw new Error('Keycloak did not return a refresh token. Check the client configuration.');
                }
                const tokens: TokenSet = {
                    accessToken: tokenResponse.access_token,
                    refreshToken,
                    expiresAt: Date.now() + tokenResponse.expires_in * 1000
                };
                const user = await fetchUserProfile(tokens.accessToken);
                applySession({ tokens, user });
                scheduleRefresh(tokens);
            } catch (error) {
                applySession(null);
                setState(prev => ({
                    ...prev,
                    error: error instanceof Error ? error.message : 'Unable to authenticate with Keycloak.',
                    isLoading: false
                }));
            }
        },
        [applySession, hasConfig, scheduleRefresh]
    );

    const loginWithGoogle = useCallback(async () => {
        if (!hasConfig) {
            setState(prev => ({
                ...prev,
                error: 'Keycloak connection details are missing. Configure the environment variables to enable login.'
            }));
            return;
        }
        try {
            ensureConfig();
            const { codeChallenge } = await generatePKCEParams();
            storeReturnPath(window.location.pathname + window.location.search);
            const baseUrl = config.KEYCLOAK_URL.replace(/\/$/, '');
            const realm = config.KEYCLOAK_REALM;
            const clientId = config.KEYCLOAK_CLIENT_ID;
            const redirectUri = encodeURIComponent(window.location.origin);

            // Redirect to Keycloak with Google IDP hint
            const authUrl = `${baseUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&code_challenge=${codeChallenge}&code_challenge_method=S256&scope=${encodeURIComponent(DEFAULT_SCOPE)}&kc_idp_hint=google`;
            
            window.location.href = authUrl;
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'Failed to initiate Google login.',
                isLoading: false
            }));
        }
    }, [hasConfig]);

    const handleOAuthCallback = useCallback(async (code: string) => {
        if (!hasConfig) {
            return;
        }
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        
        const codeVerifier = getStoredCodeVerifier();
        if (!codeVerifier) {
            setState(prev => ({
                ...prev,
                error: 'OAuth callback missing code verifier. Please try logging in again.',
                isLoading: false
            }));
            clearStoredCodeVerifier();
            return;
        }

        try {
            ensureConfig();
            const tokenResponse = await requestToken(
                buildBody(
                    {
                        grant_type: 'authorization_code',
                        code,
                        redirect_uri: window.location.origin,
                        client_id: config.KEYCLOAK_CLIENT_ID,
                        code_verifier: codeVerifier
                    },
                    false
                )
            );
            
            clearStoredCodeVerifier();
            
            const refreshToken = tokenResponse.refresh_token;
            if (!refreshToken) {
                throw new Error('Keycloak did not return a refresh token. Check the client configuration.');
            }
            
            const tokens: TokenSet = {
                accessToken: tokenResponse.access_token,
                refreshToken,
                expiresAt: Date.now() + tokenResponse.expires_in * 1000
            };
            
            const user = await fetchUserProfile(tokens.accessToken);
            applySession({ tokens, user });
            scheduleRefresh(tokens);

            // Return to the page the login was initiated from, clearing the OAuth query params
            const returnPath = getStoredReturnPath();
            clearStoredReturnPath();
            navigate(returnPath || '/', { replace: true });
        } catch (error) {
            clearStoredCodeVerifier();
            clearStoredReturnPath();
            applySession(null);
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'Failed to complete Google login.',
                isLoading: false
            }));
        }
    }, [applySession, hasConfig, navigate, scheduleRefresh]);

    const logout = useCallback(async () => {
        const refreshToken = sessionRef.current?.tokens.refreshToken;
        scheduleRefresh(null);
        applySession(null);
        if (!hasConfig || !refreshToken) {
            return;
        }
        try {
            await fetch(`${realmBase}/protocol/openid-connect/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: buildBody({
                    client_id: config.KEYCLOAK_CLIENT_ID,
                    refresh_token: refreshToken
                }).toString()
            });
        } catch (error) {
            console.warn('Keycloak logout failed', error);
        }
    }, [applySession, hasConfig, scheduleRefresh]);

    useEffect(() => {
        if (!hasConfig) {
            setState(prev => ({ ...prev, isLoading: false }));
            return;
        }

        // Check for OAuth callback
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
            clearStoredCodeVerifier();
            clearStoredReturnPath();
            setState(prev => ({
                ...prev,
                error: errorDescription || error || 'Authentication failed',
                isLoading: false
            }));
            // Clean up URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }
        
        if (code) {
            if (handledOAuthCodeRef.current === code) {
                return;
            }
            handledOAuthCodeRef.current = code;
            handleOAuthCallback(code);
            return;
        }
        
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            setState(prev => ({ ...prev, isLoading: false }));
            return;
        }
        try {
            const parsed = JSON.parse(stored) as StoredSession;
            sessionRef.current = parsed;
            if (parsed.tokens.expiresAt > Date.now()) {
                applySession(parsed);
                scheduleRefresh(parsed.tokens);
            } else if (parsed.tokens.refreshToken) {
                refreshSession(parsed.tokens.refreshToken);
            } else {
                applySession(null);
            }
        } catch (error) {
            console.warn('Failed to parse stored Keycloak session', error);
            applySession(null);
        }
    }, [applySession, hasConfig, refreshSession, scheduleRefresh, handleOAuthCallback]);

    useEffect(
        () => () => {
            clearRefreshTimer();
        },
        [clearRefreshTimer]
    );

    // Register logout handler for axios interceptors and configure axios
    useEffect(() => {
        configureAxios();
        registerLogoutHandler(async () => {
            await logout();
        });
        
        return () => {
            unregisterLogoutHandler();
        };
    }, [logout]);

    const value = useMemo<AuthContextValue>(
        () => ({
            isAuthenticated: state.isAuthenticated,
            isLoading: state.isLoading,
            user: state.user,
            error: state.error,
            accessToken: state.tokens?.accessToken ?? null,
            hasConfig,
            loginWithPassword,
            loginWithGoogle,
            logout,
            refreshSession: () => refreshSession()
        }),
        [hasConfig, loginWithPassword, loginWithGoogle, logout, refreshSession, state]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
